import { useAppStateDispatch } from '@src/providers/AppStateProvider';
import {
  ACTION,
  Button,
  FormControlSizes,
  Get,
  HamburgerMenu,
  IconName,
  MixpanelEvent,
  QuoteStatusEnum,
  RFQProtocolEnum,
  canTraderAdminForceCancelCustomerOrder,
  filterByCellValueMenuItem,
  getRowNodesToOperateOn,
  getShowJSONContextItem,
  includesEnumValue,
  useDisclosureWithContext,
  useDynamicCallback,
  useEndpointsContext,
  useGetDefaultContextMenuItems,
  useGlobalToasts,
  useJsonModal,
  useMixpanel,
  type Column,
  type ColumnDef,
  type CustomerOrder,
  type CustomerQuote,
  type DataResponse,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ICellRendererParams, MenuItemDef } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { manualRFQPricingThunks } from '../../../components/OMS/ManualRFQPricing/ManualRFQPricingSlice';
import { useFeatureFlag, useRoleAuth } from '../../../hooks';
import { useCurrentAppLayoutId } from '../../../hooks/useAppLayoutId';
import { ModifyFixingDialog, type ModifyFixingProps } from '../CareOrders/Dialogs/ModifyFixingDialog';
import { CancelS2FCustomerOrderDialog } from '../CustomerOrders/Dialogs/CancelS2FCustomerOrderDialog';
import { useCopyCustomerOrderTextMenuItem } from '../CustomerOrders/useCopyCustomerOrderTextMenuItem';
import { colIDToFilterBuilderKey } from './useCustomerQuotesFilter';

function isSendQuoteEnabled(data?: CustomerQuote) {
  if (data == null) {
    return false;
  }
  const isOpen = includesEnumValue([QuoteStatusEnum.PendingNew, QuoteStatusEnum.Open], data.QuoteStatus);
  return isOpen && data.RFQProtocol === RFQProtocolEnum.Manual;
}

function CustomerQuotesMenu({
  data,
  onShowJson,
  onSendQuote,
  ...params
}: ICellRendererParams & {
  data: CustomerQuote;
  onSendQuote?(data: CustomerQuote): void;
  onShowJson(data: CustomerQuote): void;
}) {
  return (
    <>
      {onSendQuote && (
        <Button
          disabled={!isSendQuoteEnabled(data)}
          size={FormControlSizes.Small}
          width="100%"
          ghost={true}
          onClick={() => onSendQuote(data)}
        >
          Send Quote
        </Button>
      )}
      <HamburgerMenu {...params} onShowJson={onShowJson} data={data} />
    </>
  );
}

export function useCustomerQuotesMenu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  columns: Column[];
  getContextMenuItems: (params: GetContextMenuItemsParams<CustomerQuote>) => (MenuItemDef | string)[];
  dialogs: ReactNode;
} {
  const { add: addToast } = useGlobalToasts();
  const selectedNodesRef = useRef<CustomerQuote[]>([]);
  const { orgApiEndpoint } = useEndpointsContext();
  const { isAuthorized } = useRoleAuth();

  const mixpanel = useMixpanel();
  const { handleClickJson, jsonModal } = useJsonModal();
  const {
    enableMakerMarketplaceETFRFQFlow,
    allowCancellingS2FCustomerOrder,
    enableMakerModifyFixing,
    enableModifyFixingDialogV2,
  } = useFeatureFlag();
  const navigate = useNavigate();
  // Get the last selected layout ID for the trading section
  const layoutId = useCurrentAppLayoutId();

  const showModifyFixing = isAuthorized(ACTION.EDIT_ORDER_FIXING_DETAILS) && enableMakerModifyFixing;

  const showForceCancelOrderOption =
    allowCancellingS2FCustomerOrder && isAuthorized(ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER);
  const forceCancelCustomerOrderDialog = useDisclosureWithContext<string>();

  const modifyFixingDialog = useDisclosureWithContext<ModifyFixingProps>();

  const defaultContextMenuItems = useGetDefaultContextMenuItems();
  const copyCustomerOrderExecutionTextMenuItem = useCopyCustomerOrderTextMenuItem();

  const dispatch = useAppStateDispatch();
  const handleSendQuote = useDynamicCallback((entity?: CustomerQuote) => {
    if (entity == null) {
      return;
    }
    mixpanel.track(MixpanelEvent.OpenManualPricingForm);
    dispatch(manualRFQPricingThunks.prime({ quote: entity, navigate, layoutId }));
  });

  /**
   * Given an RFQID from a CustomerQuote, makes a request to the backend and returns the OrderID for the CustomerOrder
   * associated with this CustomerQuote.
   */
  const getCustomerOrderID = useCallback(
    (rfqid: string) => {
      if (!orgApiEndpoint) {
        return undefined;
      }

      return Get<DataResponse<CustomerOrder>>(orgApiEndpoint, `/customer/orders?RFQID=${rfqid}`)
        .then(res => {
          return res.data.at(0)?.OrderID;
        })
        .catch((e: ErrorEvent) => {
          addToast({
            variant: 'NEGATIVE',
            text: `Failed to find order to cancel: ${e.message}`,
          });
        });
    },
    [orgApiEndpoint, addToast]
  );

  const handleForceCancelOrder = useCallback(
    async (rfqid: string) => {
      const orderID = await getCustomerOrderID(rfqid);
      if (orderID) {
        forceCancelCustomerOrderDialog.open(orderID);
      }
    },
    [getCustomerOrderID, forceCancelCustomerOrderDialog]
  );

  const handleModifyFixing = useCallback(
    async (quote: CustomerQuote) => {
      const customerOrderID = await getCustomerOrderID(quote.RFQID);
      if (customerOrderID) {
        modifyFixingDialog.open({
          mode: 'maker',
          quote,
          orderID: customerOrderID,
        });
      }
    },
    [getCustomerOrderID, modifyFixingDialog]
  );

  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<CustomerQuote>) => {
    selectedNodesRef.current = getRowNodesToOperateOn(params).map(node => node.data!);

    const data = params?.node?.data;

    if (data == null) {
      return [];
    }

    const groupData = params?.node?.groupData;
    if (isNil(data) && isNil(groupData)) {
      return [];
    }

    const items = compact([
      ...filterByCellValueMenuItem({ params, filterableProperties, openClause, colIDToFilterBuilderKey, mixpanel }),
      copyCustomerOrderExecutionTextMenuItem(data),
      showForceCancelOrderOption && {
        name: 'Force cancel order',
        action: () => handleForceCancelOrder(data.RFQID),
        disabled: !canTraderAdminForceCancelCustomerOrder(data),
      },
      enableMakerMarketplaceETFRFQFlow
        ? ({
            name: 'Send Quote',
            action: () => handleSendQuote(data),
            disabled: params.api.getSelectedRows().length > 1 || !isSendQuoteEnabled(data),
          } satisfies MenuItemDef)
        : undefined,
      showModifyFixing &&
        data.canModifyFixing && {
          name: 'Modify Fixing',
          action: () => handleModifyFixing(data),
          icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
          disabled: data.isMultitoken && !enableModifyFixingDialogV2,
        },
      ...(data ? [getShowJSONContextItem({ params, handleClickJson }), 'separator'] : []),
      ...defaultContextMenuItems(params),
    ]);
    return items;
  });

  const dialogs = useMemo(
    () => (
      <>
        <CancelS2FCustomerOrderDialog {...forceCancelCustomerOrderDialog} />
        <ModifyFixingDialog {...modifyFixingDialog} />
        {jsonModal}
      </>
    ),
    [jsonModal, forceCancelCustomerOrderDialog, modifyFixingDialog]
  );
  const columns = useMemo(
    () =>
      compact([
        enableMakerMarketplaceETFRFQFlow
          ? {
              type: 'button',
              id: 'sendQuote',
              width: 86,
              frozen: true,
              pinned: 'right',
              suppressColumnsToolPanel: true,
              exportable: false,
              params: {
                onClick: (params: ICellRendererParams<CustomerQuote>) => handleSendQuote(params.node.data),
                disabled: params => !isSendQuoteEnabled(params.node.data),
                size: FormControlSizes.Small,
                children: 'Send Quote',
              },
            }
          : undefined,
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => <CustomerQuotesMenu {...params} onShowJson={handleClickJson} />,
          },
        },
      ]) satisfies ColumnDef<CustomerQuote>[],
    [handleClickJson, handleSendQuote, enableMakerMarketplaceETFRFQFlow]
  );

  return {
    columns,
    getContextMenuItems,
    dialogs,
  };
}
